"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HelpAction } from "../help/faq";

type GuideStep = {
  title: string;
  body: string;
  bullets: string[];
  targetSelector?: string;
  actionLabel?: string;
  action?: HelpAction;
};

type HighlightRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  viewportW: number;
  viewportH: number;
};

type PanelPosition = {
  left: number;
  top: number;
  width: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function isVisibleElement(el: Element) {
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
}

function getFirstVisibleTarget(selector?: string) {
  if (!selector) return null;
  const matches = Array.from(document.querySelectorAll(selector));
  return matches.find(isVisibleElement) || null;
}

function rectsEqual(a: HighlightRect | null, b: HighlightRect | null) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5 &&
    a.viewportW === b.viewportW &&
    a.viewportH === b.viewportH
  );
}

function StepIcon({ index }: { index: number }) {
  const cls = "h-5 w-5";
  const common = {
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    fill: "none",
  };

  if (index === 0) {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
        <path {...common} d="M8 9h8M8 13h5" />
      </svg>
    );
  }
  if (index === 1) {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <rect {...common} x="3" y="5" width="18" height="14" rx="2" />
        <circle {...common} cx="9" cy="10" r="1.5" />
        <path {...common} d="m21 16-5-5-6 6-3-3-4 4" />
      </svg>
    );
  }
  if (index === 2) {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <path {...common} d="M6 20 12 4l6 16M8 15h8" />
      </svg>
    );
  }
  if (index === 3) {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <rect {...common} x="3" y="4" width="8" height="7" rx="1.5" />
        <rect {...common} x="13" y="4" width="8" height="7" rx="1.5" />
        <rect {...common} x="3" y="13" width="8" height="7" rx="1.5" />
        <rect {...common} x="13" y="13" width="8" height="7" rx="1.5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
      <path {...common} d="M5 12h14M13 6l6 6-6 6" />
      <path {...common} d="M5 5v14" />
    </svg>
  );
}

export default function EditorOnboardingGuide(props: {
  isOpen: boolean;
  locale: string;
  canShare: boolean;
  onClose: () => void;
  onAction?: (action: HelpAction) => void;
}) {
  const { isOpen, locale, canShare, onClose, onAction } = props;
  const lang = locale === "pl" ? "pl" : "en";
  const [stepIndex, setStepIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const steps = useMemo<GuideStep[]>(() => {
    if (lang === "pl") {
      return [
        {
          title: "Jak czytać stronę",
          body: "Canvas pokazuje obszar projektu razem ze spadem, żeby łatwiej przygotować plik do druku.",
          targetSelector: '[data-onboarding-target="canvas"]',
          bullets: [
            "Szara strefa to spad. Tła i zdjęcia powinny dochodzić do tej części.",
            "Pomarańczowa linia pokazuje linię cięcia.",
            "Tekst trzymaj wewnątrz własnej ramki i z dala od brzegu."
          ],
        },
        {
          title: "Dodawanie zdjęć i elementów",
          body: "Z lewego paska dodajesz zdjęcia, cliparty, kształty, tła i inne elementy projektu.",
          targetSelector: '[data-onboarding-target="tool-image"], [data-onboarding-target="mobile-tool-image"]',
          bullets: [
            "Kliknij narzędzie, a potem wybierz element z panelu.",
            "Po dodaniu możesz przeciągać, obracać i zmieniać rozmiar.",
            "Miniatury stron pomagają szybko wrócić do miejsc z treścią."
          ],
          actionLabel: "Otwórz Zdjęcia",
          action: { type: "open_tool", tool: "image" },
        },
        {
          title: "Tekst i edycja",
          body: "Tekst po dodaniu można od razu przesuwać. Dwuklik uruchamia pisanie.",
          targetSelector: '[data-onboarding-target="tool-text"], [data-onboarding-target="mobile-tool-text"]',
          bullets: [
            "Kliknij tekst, żeby zobaczyć właściwości po prawej stronie.",
            "Zmienisz tam czcionkę, rozmiar, kolor i odstępy.",
            "Uchwyty wokół elementu służą do skalowania i obrotu."
          ],
          actionLabel: "Otwórz Tekst",
          action: { type: "open_tool", tool: "text" },
        },
        {
          title: "Szablony i rozkładówki",
          body: "Szablony przyspieszają składanie projektu, a rozkładówki dodają gotowy układ strony.",
          targetSelector: '[data-onboarding-target="tool-templates"], [data-onboarding-target="mobile-tool-templates"]',
          bullets: [
            "Motywy tworzą większy startowy układ projektu.",
            "Rozkładówki możesz wstawiać pojedynczo.",
            "Podglądy pomagają wybrać układ bez zgadywania po nazwie."
          ],
          actionLabel: "Otwórz Rozkładówki",
          action: { type: "open_templates", tab: "spreads" },
        },
        {
          title: "Zapis i zamówienie",
          body: "Edytor zapisuje projekt roboczo, ale przed zamówieniem zapisuje aktualny stan jeszcze raz.",
          targetSelector: '[data-onboarding-target="save-button"], [data-onboarding-target="order-button"]',
          bullets: [
            "Użyj Zapisz, gdy chcesz wymusić zapis ręcznie.",
            "Udostępnij pozwala zaprosić drugą osobę do projektu.",
            "Zamów otwiera w sklepie podgląd książki do sprawdzenia przed zakupem."
          ],
          actionLabel: canShare ? "Otwórz Udostępnij" : undefined,
          action: canShare ? { type: "open_share" } : undefined,
        },
      ];
    }

    return [
      {
        title: "Reading the page",
        body: "The canvas shows the design area together with bleed, so the file is easier to prepare for print.",
        targetSelector: '[data-onboarding-target="canvas"]',
        bullets: [
          "The gray area is bleed. Backgrounds and photos should extend into it.",
          "The orange line marks the trim line.",
          "Keep text inside its own frame and away from the edge."
        ],
      },
      {
        title: "Adding photos and elements",
        body: "Use the left toolbar to add photos, clipart, shapes, backgrounds and other design elements.",
        targetSelector: '[data-onboarding-target="tool-image"], [data-onboarding-target="mobile-tool-image"]',
        bullets: [
          "Open a tool, then choose an item from its panel.",
          "After adding it, you can drag, rotate and resize it.",
          "Page thumbnails help you jump back to pages with content."
        ],
        actionLabel: "Open Images",
        action: { type: "open_tool", tool: "image" },
      },
      {
        title: "Text and editing",
        body: "New text can be moved immediately. Double-click it when you want to type.",
        targetSelector: '[data-onboarding-target="tool-text"], [data-onboarding-target="mobile-tool-text"]',
        bullets: [
          "Click text to show its properties on the right.",
          "Change font, size, color and spacing there.",
          "Handles around an element resize and rotate it."
        ],
        actionLabel: "Open Text",
        action: { type: "open_tool", tool: "text" },
      },
      {
        title: "Templates and spreads",
        body: "Templates speed up layout work, and spreads add a ready page layout.",
        targetSelector: '[data-onboarding-target="tool-templates"], [data-onboarding-target="mobile-tool-templates"]',
        bullets: [
          "Themes create a larger starting layout.",
          "Spreads can be inserted one by one.",
          "Previews help you choose a layout without guessing from a name."
        ],
        actionLabel: "Open Spreads",
        action: { type: "open_templates", tab: "spreads" },
      },
      {
        title: "Save and order",
        body: "The editor autosaves drafts, and saves the latest state again before order handoff.",
        targetSelector: '[data-onboarding-target="save-button"], [data-onboarding-target="order-button"]',
        bullets: [
          "Use Save when you want to force a manual save.",
          "Share lets you invite another person to the project.",
          "Order opens your book preview in the shop for review before purchase."
        ],
        actionLabel: canShare ? "Open Share" : undefined,
        action: canShare ? { type: "open_share" } : undefined,
      },
    ];
  }, [canShare, lang]);

  useEffect(() => {
    if (isOpen) setStepIndex(0);
  }, [isOpen]);

  const step = steps[stepIndex] || steps[0];
  const isLast = stepIndex >= steps.length - 1;
  const updateCoachmark = useCallback(() => {
    if (!isOpen) return;
    const target = getFirstVisibleTarget(step.targetSelector);
    if (!target) {
      setHighlightRect((prev) => (prev === null ? prev : null));
      setPanelPosition((prev) => (prev === null ? prev : null));
      return;
    }

    const rect = target.getBoundingClientRect();
    const pad = 10;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const nextRect: HighlightRect = {
      left: clamp(rect.left - pad, 8, Math.max(8, viewportW - 16)),
      top: clamp(rect.top - pad, 8, Math.max(8, viewportH - 16)),
      width: Math.min(rect.width + pad * 2, Math.max(0, viewportW - 16)),
      height: Math.min(rect.height + pad * 2, Math.max(0, viewportH - 16)),
      viewportW,
      viewportH,
    };

    setHighlightRect((prev) => (rectsEqual(prev, nextRect) ? prev : nextRect));

    const panelWidth = Math.min(560, viewportW - 32);
    const measuredH = panelRef.current?.getBoundingClientRect().height || 470;
    const gap = 18;
    const spaceRight = viewportW - (nextRect.left + nextRect.width);
    const spaceLeft = nextRect.left;
    let left: number;
    let top: number;

    if (viewportW < 768) {
      left = 16;
      top = nextRect.top > measuredH + 40
        ? nextRect.top - measuredH - gap
        : Math.min(nextRect.top + nextRect.height + gap, viewportH - measuredH - 16);
    } else if (spaceRight >= panelWidth + gap + 16) {
      left = nextRect.left + nextRect.width + gap;
      top = nextRect.top + nextRect.height / 2 - measuredH / 2;
    } else if (spaceLeft >= panelWidth + gap + 16) {
      left = nextRect.left - panelWidth - gap;
      top = nextRect.top + nextRect.height / 2 - measuredH / 2;
    } else {
      left = clamp(nextRect.left + nextRect.width / 2 - panelWidth / 2, 16, viewportW - panelWidth - 16);
      top = nextRect.top > measuredH + 40
        ? nextRect.top - measuredH - gap
        : nextRect.top + nextRect.height + gap;
    }

    const nextPanel = {
      left: clamp(left, 16, Math.max(16, viewportW - panelWidth - 16)),
      top: clamp(top, 16, Math.max(16, viewportH - measuredH - 16)),
      width: panelWidth,
    };

    setPanelPosition((prev) =>
      prev &&
      Math.abs(prev.left - nextPanel.left) < 0.5 &&
      Math.abs(prev.top - nextPanel.top) < 0.5 &&
      Math.abs(prev.width - nextPanel.width) < 0.5
        ? prev
        : nextPanel
    );
  }, [isOpen, step.targetSelector]);

  useEffect(() => {
    if (!isOpen) return;
    updateCoachmark();
    const raf = window.requestAnimationFrame(updateCoachmark);
    window.addEventListener("resize", updateCoachmark);
    window.addEventListener("scroll", updateCoachmark, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateCoachmark);
      window.removeEventListener("scroll", updateCoachmark, true);
    };
  }, [isOpen, stepIndex, updateCoachmark]);

  useEffect(() => {
    if (!isOpen) return;
    const raf = window.requestAnimationFrame(updateCoachmark);
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen, stepIndex, updateCoachmark]);

  const guidePanelClass = panelPosition
    ? "fixed overflow-hidden rounded-2xl border border-white/10 bg-[#0f141d] text-white shadow-2xl"
    : "absolute inset-x-4 top-1/2 mx-auto w-full max-w-[560px] -translate-y-1/2 overflow-hidden rounded-2xl border border-white/10 bg-[#0f141d] text-white shadow-2xl";
  const guidePanelStyle = panelPosition
    ? { left: panelPosition.left, top: panelPosition.top, width: panelPosition.width }
    : undefined;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[12000]">
      {highlightRect ? (
        <>
          <div
            className="fixed left-0 top-0 bg-black/50"
            style={{ width: highlightRect.viewportW, height: highlightRect.top }}
          />
          <div
            className="fixed left-0 bg-black/50"
            style={{
              top: highlightRect.top + highlightRect.height,
              width: highlightRect.viewportW,
              height: Math.max(0, highlightRect.viewportH - highlightRect.top - highlightRect.height),
            }}
          />
          <div
            className="fixed bg-black/50"
            style={{
              left: 0,
              top: highlightRect.top,
              width: highlightRect.left,
              height: highlightRect.height,
            }}
          />
          <div
            className="fixed bg-black/50"
            style={{
              left: highlightRect.left + highlightRect.width,
              top: highlightRect.top,
              width: Math.max(0, highlightRect.viewportW - highlightRect.left - highlightRect.width),
              height: highlightRect.height,
            }}
          />
          <div
            className="pointer-events-none fixed rounded-2xl border-2 border-[#00adad] shadow-[0_0_0_9999px_rgba(0,0,0,0.08),0_0_0_6px_rgba(0,173,173,0.18),0_18px_42px_rgba(0,0,0,0.35)]"
            style={{
              left: highlightRect.left,
              top: highlightRect.top,
              width: highlightRect.width,
              height: highlightRect.height,
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/45" />
      )}
      <div ref={panelRef} className={guidePanelClass} style={guidePanelStyle}>
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">
              {lang === "pl" ? "Przewodnik" : "Guide"}
            </div>
            <div className="mt-1 text-lg font-semibold">
              {lang === "pl" ? "Pierwsze kroki w edytorze" : "First steps in the editor"}
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/5 hover:text-white"
            onClick={onClose}
            aria-label={lang === "pl" ? "Zamknij przewodnik" : "Close guide"}
            title={lang === "pl" ? "Zamknij" : "Close"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="mb-5 flex gap-2" aria-hidden="true">
            {steps.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 flex-1 rounded-full ${idx <= stepIndex ? "bg-white" : "bg-white/15"}`}
              />
            ))}
          </div>

          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white">
              <StepIcon index={stepIndex} />
            </div>
            <div className="min-w-0">
              <div className="text-xl font-semibold">{step.title}</div>
              <p className="mt-2 text-sm leading-6 text-white/78">{step.body}</p>
              <ul className="mt-4 space-y-2 text-sm leading-6 text-white/78">
                {step.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-2">
                    <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#00adad]" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="text-left text-sm text-white/60 transition-colors hover:text-white"
            onClick={onClose}
          >
            {lang === "pl" ? "Pomiń przewodnik" : "Skip guide"}
          </button>
          <div className="flex flex-col gap-2 sm:flex-row">
            {step.action && step.actionLabel ? (
              <button
                type="button"
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
                onClick={() => {
                  onClose();
                  onAction?.(step.action!);
                }}
              >
                {step.actionLabel}
              </button>
            ) : null}
            {stepIndex > 0 ? (
              <button
                type="button"
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
                onClick={() => setStepIndex((idx) => Math.max(0, idx - 1))}
              >
                {lang === "pl" ? "Wstecz" : "Back"}
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-lg border border-[#00adad] bg-[linear-gradient(135deg,#00adad,#005a5a)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-900/20"
              onClick={() => {
                if (isLast) {
                  onClose();
                } else {
                  setStepIndex((idx) => Math.min(steps.length - 1, idx + 1));
                }
              }}
            >
              {isLast
                ? lang === "pl"
                  ? "Zakończ"
                  : "Finish"
                : lang === "pl"
                  ? "Dalej"
                  : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
